import { ArrowRight, CheckCircle2, MapPin, Clock3, Sparkles, WalletCards } from "lucide-react";
import Navigation from "@/components/Navigation";
import ContactSection from "@/components/ContactSection";
import Footer from "@/components/Footer";
import MobileStickyBar from "@/components/MobileStickyBar";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import Seo, { businessSchema } from "@/components/seo/Seo";

export default function CoveringLyonPage() {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Covering cuisine Lyon et métropole",
    provider: { "@type": "Organization", name: "Vinylux", url: "https://www.vinylux-covering.fr/" },
    areaServed: ["Lyon", "Villeurbanne", "Caluire-et-Cuire", "Rillieux-la-Pape", "Métropole de Lyon"],
    description: "Relooking et rénovation de cuisine par covering à Lyon et dans sa métropole.",
    url: "https://www.vinylux-covering.fr/covering-cuisine-lyon",
  };

  return (
    <div className="min-h-screen bg-[#0284FC]">
      <Seo
        title="Covering cuisine Lyon | Relooking sans remplacement – Vinylux"
        description="À Lyon et dans sa métropole, Vinylux modernise les cuisines par covering : façades, meubles et surfaces compatibles. Une alternative au remplacement complet."
        path="/covering-cuisine-lyon"
        schema={[businessSchema, serviceSchema]}
      />
      <Navigation />
      <main className="pb-20 lg:pb-0">
        <section className="pt-32 lg:pt-[188px] pb-20 lg:pb-28 bg-[#026FD4]">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 bg-white/15 text-white px-4 py-1.5 rounded-full text-sm font-semibold mb-7">
                <MapPin className="w-4 h-4" /> Lyon & métropole
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.05] mb-6">
                Relooker une cuisine à Lyon sans repartir de zéro
              </h1>
              <p className="text-lg text-white/80 leading-relaxed mb-8 max-w-3xl">
                Si l'implantation vous convient mais que la finition a vieilli, le covering peut offrir une transformation nette avec beaucoup moins de dépose qu'un remplacement complet.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#contact" className="inline-flex items-center justify-center gap-2 bg-white text-[#0284FC] font-bold px-8 py-4 rounded-full hover:scale-105 transition-all">Étudier mon projet <ArrowRight className="w-5 h-5" /></a>
                <a href="/realisations" className="inline-flex items-center justify-center border-2 border-white/30 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 transition-all">Voir les avant / après</a>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-5 mt-14">
              {[
                { icon: Clock3, title: "Moins de dépose", text: "On conserve les éléments qui peuvent l'être au lieu de repartir sur une cuisine complète." },
                { icon: Sparkles, title: "Transformation visuelle", text: "Unies, bois, pierre ou effets matière : le style peut changer radicalement." },
                { icon: WalletCards, title: "Budget maîtrisé", text: "Le devis dépend des surfaces et de la préparation, pas d'un forfait artificiel." },
              ].map(({ icon: Icon, title, text }) => <div key={title} className="bg-white/10 border border-white/15 rounded-2xl p-6"><Icon className="w-7 h-7 text-white mb-4" /><h2 className="font-bold text-white text-xl mb-2">{title}</h2><p className="text-white/70 leading-relaxed">{text}</p></div>)}
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-28 bg-[#0284FC]">
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-start">
            <div className="bg-white rounded-3xl p-8 lg:p-10 text-[#035EB2]">
              <h2 className="font-heading text-3xl font-bold mb-6">Zones desservies autour de Lyon</h2>
              <div className="flex flex-wrap gap-3">
                {["Lyon", "Villeurbanne", "Caluire-et-Cuire", "Rillieux-la-Pape", "Neuville-sur-Saône", "Nord lyonnais"].map((zone) => <span key={zone} className="inline-flex items-center gap-2 bg-[#EAF5FF] px-4 py-2 rounded-full text-sm font-semibold"><MapPin className="w-4 h-4" />{zone}</span>)}
              </div>
              <p className="mt-7 text-[#035EB2]/70 leading-relaxed">Pour les communes plus éloignées, la faisabilité du déplacement est validée avec le projet.</p>
            </div>

            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-7">Dans quels cas le covering est pertinent ?</h2>
              <div className="space-y-4">
                {[
                  ["La cuisine est fonctionnelle", "Les caissons, charnières et façades sont encore suffisamment sains pour être conservés."],
                  ["Vous voulez surtout changer le style", "Couleur, effet bois, pierre ou finition mate : l'objectif est principalement esthétique."],
                  ["Vous voulez limiter les travaux", "Moins de dépose signifie généralement moins de perturbation qu'un remplacement intégral."],
                  ["Le support est compatible", "C'est le point non négociable : l'état et la géométrie du support déterminent si le résultat sera durable."],
                ].map(([title, text]) => <div key={title} className="flex gap-4 bg-white/10 border border-white/15 rounded-2xl p-5"><CheckCircle2 className="w-6 h-6 text-white shrink-0 mt-0.5" /><div><h3 className="font-bold text-white mb-1">{title}</h3><p className="text-white/70 leading-relaxed">{text}</p></div></div>)}
              </div>
            </div>
          </div>
        </section>

        <BeforeAfterSlider />
        <ContactSection />
      </main>
      <Footer />
      <MobileStickyBar />
    </div>
  );
}
